import { useCallback, useEffect, useState } from 'react';
import {
  deposerPieceJointeModele,
  getModelesEmail,
  supprimerPieceJointeModele,
  updateModeleEmail,
} from '../services/modeleEmailService';
import RemplirParTexte from '../components/RemplirParTexte';

import '../assets/styles/Parametrage.css';

/** Taille lisible d'un fichier. */
function tailleLisible(octets) {
  if (!octets && octets !== 0) return '';
  return octets < 1024 * 1024
    ? `${Math.round(octets / 1024)} Ko`
    : `${(octets / (1024 * 1024)).toFixed(1)} Mo`;
}

/**
 * Document que chaque email joint. Le rappel n'en a pas : il ne transmet
 * aucune pièce, il signale seulement que la session approche — et
 * l'application ne produit d'ailleurs aucun PDF de rappel.
 */
const DOCUMENT_JOINT = {
  devis: 'le PDF du devis',
  devis_relance: 'le PDF du devis',
  contrat: 'le PDF du contrat',
  contrat_relance: 'le PDF du contrat',
  feuille_presence_signee: 'la feuille de présence émargée',
  convocation: 'le PDF de la convocation',
  rappel: null,
};

/**
 * Réglage de l'email d'une page, ouvert depuis son bouton « Paramétrage
 * email ».
 *
 * Un modèle par page, et un seul : chaque écran gouverne l'envoi qui le
 * concerne. Le paramétrage n'est plus regroupé sur une page commune, où il
 * fallait retrouver le bon modèle parmi cinq.
 *
 * `type` désigne la ligne de modeles_email (cf. migration 023).
 */
function ModeleEmailPanneau({ type, onClose }) {
  const [modele, setModele] = useState(null);
  const [balises, setBalises] = useState([]);
  const [blocs, setBlocs] = useState([]);
  const [brouillon, setBrouillon] = useState(null);

  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  // Dépôt ou retrait de la pièce jointe fixe en cours.
  const [pieceEnCours, setPieceEnCours] = useState(false);

  async function deposerPiece(fichier) {
    if (!fichier) return;
    setPieceEnCours(true);
    setMessage(null);
    try {
      const res = await deposerPieceJointeModele(type, fichier);
      setModele((prec) => ({ ...prec, ...res.data }));
      setMessage({ ok: true, texte: `« ${fichier.name} » sera joint à chaque email de ce modèle.` });
    } catch (err) {
      setMessage({ ok: false, texte: err.message || 'Dépôt impossible.' });
    } finally {
      setPieceEnCours(false);
    }
  }

  async function retirerPiece() {
    if (!window.confirm('Retirer ce fichier ? Les prochains emails partiront sans lui.')) return;
    setPieceEnCours(true);
    setMessage(null);
    try {
      const res = await supprimerPieceJointeModele(type);
      setModele((prec) => ({ ...prec, ...res.data }));
      setMessage({ ok: true, texte: 'Pièce jointe retirée.' });
    } catch (err) {
      setMessage({ ok: false, texte: err.message || 'Retrait impossible.' });
    } finally {
      setPieceEnCours(false);
    }
  }

  const charger = useCallback(() => {
    setLoading(true);
    setErreur(null);
    getModelesEmail()
      .then((res) => {
        const trouve = (res.data || []).find((m) => m.type === type);
        if (!trouve) throw new Error(`Modèle « ${type} » introuvable.`);
        setModele(trouve);
        setBalises(res.balises || []);
        setBlocs(res.blocs || []);
        setBrouillon({
          objet: trouve.objet,
          contenu: trouve.contenu,
          joindreDocument: trouve.joindre_document,
        });
      })
      .catch((err) => setErreur(err.message || "Impossible de charger le modèle d'email."))
      .finally(() => setLoading(false));
  }, [type]);

  useEffect(charger, [charger]);

  // Échap ferme le panneau, comme les autres modales de l'application.
  useEffect(() => {
    const surEchap = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', surEchap);
    return () => window.removeEventListener('keydown', surEchap);
  }, [onClose]);

  const modifie =
    modele &&
    brouillon &&
    (brouillon.objet !== modele.objet ||
      brouillon.contenu !== modele.contenu ||
      brouillon.joindreDocument !== modele.joindre_document);

  function modifier(champ, valeur) {
    setBrouillon((prec) => ({ ...prec, [champ]: valeur }));
    setMessage(null);
  }

  async function enregistrer() {
    setSaving(true);
    setMessage(null);
    try {
      const res = await updateModeleEmail(type, brouillon);
      setModele(res.data);
      setMessage({ ok: true, texte: 'Modèle enregistré.' });
    } catch (err) {
      setMessage({ ok: false, texte: err.message || 'Enregistrement impossible.' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="param-modal-overlay" onClick={onClose}>
      <div className="param-modal" onClick={(e) => e.stopPropagation()}>
        <div className="param-modal-header">
          <div>
            <div className="param-modal-title">
              {modele ? modele.libelle : 'Paramétrage email'}
            </div>
            {modele && <div className="param-card-note">{modele.description}</div>}
          </div>
          <button
            type="button"
            className="param-modal-close"
            onClick={onClose}
            aria-label="Fermer"
          >
            ×
          </button>
        </div>

        <div className="param-modal-body">
          {loading && <div className="param-state">Chargement du modèle...</div>}

          {!loading && erreur && <div className="param-state param-state-error">{erreur}</div>}

          {!loading && !erreur && brouillon && (
            <div className="param-form">
              <RemplirParTexte
                variante="param"
                contexte="modèle d'email (objet et contenu) ; conserver telles quelles les balises {{…}} et les blocs {{#…}} … {{/…}} du texte, sans les traduire ni les reformuler"
                champs={[
                  { cle: 'objet', libelle: 'Objet' },
                  { cle: 'contenu', libelle: 'Contenu' },
                ]}
                onValeurs={(v) => {
                  setBrouillon((prec) => ({ ...prec, ...v }));
                  setMessage(null);
                }}
              />

              {balises.length > 0 && (
                <div className="param-hint">
                  Balises disponibles :{' '}
                  {balises.map((b, i) => (
                    <span key={b.cle}>
                      {i > 0 && ' · '}
                      <span className="param-code" title={b.description}>{`{{${b.cle}}}`}</span>
                    </span>
                  ))}
                </div>
              )}

              {/* Les blocs ont leur propre syntaxe : on les explique à part
                  plutôt que de les mêler aux balises simples. */}
              {blocs.length > 0 && (
                <div className="param-hint">
                  Blocs conditionnels :{' '}
                  {blocs.map((b, i) => (
                    <span key={b.cle}>
                      {i > 0 && ' · '}
                      <span className="param-code" title={b.description}>
                        {`{{#${b.cle}}} … {{/${b.cle}}}`}
                      </span>
                    </span>
                  ))}
                  {' — '}affichés seulement si la balise a une valeur.
                </div>
              )}

              <div className="param-field">
                <label className="param-label" htmlFor={`objet-${type}`}>
                  Objet
                </label>
                <input
                  id={`objet-${type}`}
                  className="param-input"
                  value={brouillon.objet}
                  onChange={(e) => modifier('objet', e.target.value)}
                  disabled={saving}
                />
              </div>

              <div className="param-field">
                <label className="param-label" htmlFor={`contenu-${type}`}>
                  Contenu
                </label>
                <textarea
                  id={`contenu-${type}`}
                  className="param-textarea"
                  rows={10}
                  value={brouillon.contenu}
                  onChange={(e) => modifier('contenu', e.target.value)}
                  disabled={saving}
                />
              </div>

              {/* Pas de case pour le rappel : il n'existe aucun document à
                  joindre, la proposer laisserait croire le contraire. */}
              {DOCUMENT_JOINT[type] ? (
                <label className="param-checkbox-field">
                  <input
                    type="checkbox"
                    checked={Boolean(brouillon.joindreDocument)}
                    onChange={(e) => modifier('joindreDocument', e.target.checked)}
                    disabled={saving}
                  />
                  <span>Joindre {DOCUMENT_JOINT[type]}</span>
                </label>
              ) : (
                <div className="param-hint">
                  Aucun document à joindre.
                </div>
              )}

              {/* Pièce jointe fixe : un PDF déposé une fois (catalogue des
                  formations, CGV) et envoyé avec chaque email de ce modèle.
                  Le bloc {{#piece_jointe}}…{{/piece_jointe}} du texte ne
                  s'imprime que s'il y en a une. */}
              <div className="param-field">
                <span className="param-label">Pièce jointe fixe (catalogue, CGV…)</span>
                {modele.piece_jointe_nom ? (
                  <div className="param-form-message">
                    <strong>{modele.piece_jointe_nom}</strong>
                    {modele.piece_jointe_octets != null ? (
                      <span className="devis-status-detail"> · {tailleLisible(modele.piece_jointe_octets)}</span>
                    ) : (
                      <span className="devis-status-detail"> · fichier introuvable sur le serveur</span>
                    )}
                    {modele.piece_jointe_defaut && (
                      <span className="param-pill" style={{ marginLeft: 8 }}>
                        Catalogue livré avec l&apos;application
                      </span>
                    )}
                    <div className="devis-status-detail" style={{ marginTop: 4 }}>
                      Jointe à chaque email ; active le bloc{' '}
                      <code>{'{{#piece_jointe}}'}…{'{{/piece_jointe}}'}</code>.
                      {modele.piece_jointe_defaut &&
                        ' Déposez un PDF pour la remplacer.'}
                    </div>
                  </div>
                ) : (
                  <div className="param-hint">
                    Aucun fichier. Un PDF déposé (25 Mo max) part avec chaque email et active{' '}
                    <code>{'{{#piece_jointe}}'}…{'{{/piece_jointe}}'}</code>.
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <label className="param-btn" style={{ cursor: pieceEnCours ? 'wait' : 'pointer' }}>
                    {pieceEnCours
                      ? 'Envoi en cours…'
                      : modele.piece_jointe_nom
                        ? 'Remplacer le PDF'
                        : 'Déposer un PDF'}
                    <input
                      type="file"
                      accept="application/pdf,.pdf"
                      style={{ display: 'none' }}
                      disabled={pieceEnCours || saving}
                      onChange={(e) => {
                        deposerPiece(e.target.files?.[0]);
                        e.target.value = '';
                      }}
                    />
                  </label>
                  {/* Le catalogue livré avec l'application ne se retire pas
                      d'ici : on le remplace. Seul un fichier déposé se retire. */}
                  {modele.piece_jointe_nom && !modele.piece_jointe_defaut && (
                    <button
                      type="button"
                      className="param-btn param-btn-danger"
                      onClick={retirerPiece}
                      disabled={pieceEnCours || saving}
                    >
                      Retirer
                    </button>
                  )}
                </div>
              </div>

              {message && (
                <div
                  className={
                    'param-form-message' +
                    (message.ok ? ' param-form-message-success' : ' param-form-message-error')
                  }
                >
                  {message.texte}
                </div>
              )}

              <div className="param-form-actions">
                <button type="button" className="param-btn" onClick={onClose} disabled={saving}>
                  Fermer
                </button>
                <button
                  type="button"
                  className="param-btn param-btn-primary"
                  onClick={enregistrer}
                  disabled={!modifie || saving}
                >
                  {saving ? 'Enregistrement' : 'Enregistrer'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ModeleEmailPanneau;
